<template>
  <woot-modal :show="show" :on-close="handleClose">
    <woot-modal-header
      :header-title="isEditing ? $t('BUSINESS_DATA.AI_RULES.EDIT') : $t('BUSINESS_DATA.AI_RULES.ADD_NEW')"
    />
    <form class="flex flex-col modal-content" @submit.prevent="handleSubmit">
      <div class="space-y-5">
        <div class="space-y-2">
          <woot-input
            v-model="form.title"
            :label="$t('BUSINESS_DATA.AI_RULES.TITLE_LABEL')"
            :required="true"
            :placeholder="$t('BUSINESS_DATA.AI_RULES.TITLE_PLACEHOLDER')"
            :error="v$.form.title.$error ? $t('BUSINESS_DATA.AI_RULES.TITLE_ERROR') : ''"
            :class="{ error: v$.form.title.$error }"
            type="text"
            maxlength="150"
            @blur="v$.form.title.$touch"
          />
          <div v-if="!v$.form.title.$error" class="flex justify-end text-xs">
            <span :class="form.title.length > 180 ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
              {{ form.title.length }}/150
            </span>
          </div>
        </div>

        <div class="space-y-2">
          <FormLabel
            :label="$t('BUSINESS_DATA.AI_RULES.DESCRIPTION_LABEL')"
            :required="true"
            :has-error="v$.form.description.$error"
          />
          <textarea
            v-model="form.description"
            :placeholder="$t('BUSINESS_DATA.AI_RULES.DESCRIPTION_PLACEHOLDER')"
            :class="[
              'w-full px-3 py-2 rounded-lg transition-colors duration-200 resize-y min-h-[100px]',
              { 'border-red-500 focus:border-red-500 dark:border-red-400 dark:focus:border-red-400': v$.form.description.$error }
            ]"
            rows="4"
            required
            maxlength="2000"
            @blur="v$.form.description.$touch"
          />
          <div v-if="v$.form.description.$error" class="text-xs text-red-600 dark:text-red-400">
            {{ $t('BUSINESS_DATA.AI_RULES.DESCRIPTION_ERROR') }}
          </div>
          <div v-else class="flex justify-end text-xs">
            <span :class="form.description.length > 1800 ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
              {{ form.description.length }}/2000
            </span>
          </div>
        </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button type="button" variant="smooth" color-scheme="ghost" @click="handleClose">
          {{ $t('COMMON.CANCEL') }}
        </woot-button>

        <woot-button
          type="submit"
          variant="smooth"
          color-scheme="success"
          :is-loading="loading"
          :disabled="!isValid || loading"
        >
          {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

export default {
  name: 'AiRuleModal',
  setup() {
    return { v$: useVuelidate() };
  },
  components: { FormLabel },
  props: {
    show: { type: Boolean, default: false },
    rule: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  },
  emits: ['close', 'save'],
  data() {
    return {
      form: {
        title: '',
        description: ''
      }
    }
  },
  computed: {
    isEditing() {
      return !!this.rule
    },
    isValid() {
      return this.form.title.trim().length > 0 && this.form.description.trim().length > 0
    }
  },
  validations: {
    form: {
      title: { required, maxLength: maxLength(200) },
      description: { required, maxLength: maxLength(2000) },
    },
  },
  watch: {
    rule: {
      immediate: true,
      handler(newRule) {
        if (newRule) {
          this.form = {
            title: newRule.title || '',
            description: newRule.description || ''
          }
        } else {
          this.resetForm()
        }
      }
    },
    show(newValue) {
      if (!newValue) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.form = { title: '', description: '' }
      if (this.v$ && typeof this.v$.$reset === 'function') {
        this.v$.$reset()
      }
    },
    handleClose() {
      if (this.v$ && typeof this.v$.$reset === 'function') {
        this.v$.$reset();
      }
      this.$emit('close')
    },
    handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isValid || this.loading) {
        return
      }
      this.$emit('save', { ...this.form })
    }
  }
}
</script>
